import { getDefaultStore } from 'jotai';
import { authTokensAtom, authUserAtom, authErrorAtom } from '../atoms/authAtoms';
import { API_BASE_URL } from './endpoints';
import { queryClient } from './queryClient';
import { ApiError } from '../types';
import type { AuthTokens } from '../types';
import * as authService from '../features/auth/authService';

const store = getDefaultStore();
const TOKEN_REFRESH_SKEW_MS = 30_000;

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

function getErrorMessage(text: string): string {
  if (!text) {
    return 'Unexpected API error';
  }

  try {
    const parsed = JSON.parse(text) as { message?: string };
    if (typeof parsed.message === 'string' && parsed.message.trim()) {
      return parsed.message;
    }
  } catch {
    // Fall back to the raw response text when the payload is not JSON.
  }

  return text;
}

export async function getRequestTokens(): Promise<AuthTokens | null> {
  const currentTokens = store.get(authTokensAtom);

  if (!currentTokens) {
    return null;
  }

  if (currentTokens.expiresAt > Date.now() + TOKEN_REFRESH_SKEW_MS) {
    return currentTokens;
  }

  const refreshedTokens = await authService.getCurrentSession();

  if (!refreshedTokens) {
    store.set(authTokensAtom, null);
    store.set(authUserAtom, null);
    return null;
  }

  store.set(authTokensAtom, refreshedTokens);
  store.set(authUserAtom, authService.extractUserFromIdToken(refreshedTokens.idToken));

  return refreshedTokens;
}

export function clearAuthForUnauthorized(): void {
  queryClient.clear();
  store.set(authTokensAtom, null);
  store.set(authUserAtom, null);
  store.set(authErrorAtom, 'Session expired. Please sign in again.');
}

function createApiClient(baseUrl: string) {
  async function request<T>(method: HttpMethod, path: string, body?: unknown): Promise<T> {
    const tokens = await getRequestTokens();

    const headers: HeadersInit = {
      Accept: 'application/json',
      'Cache-Control': 'no-store, no-cache, max-age=0, must-revalidate',
      'Content-Type': 'application/json',
      Expires: '0',
      Pragma: 'no-cache',
    };

    if (tokens?.idToken) {
      headers.Authorization = tokens.idToken;
    }

    const response = await fetch(`${baseUrl}${path}`, {
      method,
      headers,
      cache: 'no-store',
      ...(body ? { body: JSON.stringify(body) } : {}),
    });

    if (response.status === 401) {
      clearAuthForUnauthorized();
    }

    if (!response.ok) {
      const text = await response.text();
      throw new ApiError(getErrorMessage(text), response.status);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return (await response.json()) as T;
  }

  return {
    get<T>(path: string) {
      return request<T>('GET', path);
    },
    post<T>(path: string, body: unknown) {
      return request<T>('POST', path, body);
    },
    put<T>(path: string, body: unknown) {
      return request<T>('PUT', path, body);
    },
    patch<T>(path: string, body: unknown) {
      return request<T>('PATCH', path, body);
    },
    delete<T>(path: string) {
      return request<T>('DELETE', path);
    },
  };
}

export const apiClient = createApiClient(API_BASE_URL);
export const conversationApiClient = createApiClient(API_BASE_URL);
export const recordingApiClient = createApiClient(API_BASE_URL);
export const registerApiClient = createApiClient(API_BASE_URL);
export { createApiClient };
